/* eslint-disable @typescript-eslint/no-explicit-any */
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Button, Types } from '@marriott/mi-ui-library';
import { useGetBreakpoint } from '../../../../modules/utils';
import clsx from 'clsx';
import {
  allergenType,
  dietType,
  footerDisclaimerLimit,
  menuFooterListDesktop,
  menuFooterListMobile,
} from '../../../../modules/utils/constants';
import DietAllergenIcon from '../DietAllergenIcon/DietAllergenIcon';
import { StyledMenuModalFooter } from './MenuModalFooter.styles';
import { WatchQueryFetchPolicy, useLazyQuery } from '@apollo/client';
import { lookupMenuAllergens, lookupMenuDiets } from '@marriott/mi-awp-graphql';
import Cookies from 'js-cookie';
import { PageParamsContext } from '../../../../modules/context';

export const MenuModalFooter = (props: any) => {
  let portSize = useGetBreakpoint();
  const [isMobileViewPort, setIsMobileViewPort] = useState(portSize === 'mobile' || portSize === 'tablet');
  const [menuLimit, setMenuLimit] = useState(isMobileViewPort ? menuFooterListMobile : menuFooterListDesktop);
  const footerDisclaimer =
    props?.footerDisclaimer?.length <= footerDisclaimerLimit
      ? props?.footerDisclaimer
      : props?.footerDisclaimer?.slice(0, footerDisclaimerLimit);
  const pageContext = useContext(createContext<any>({}));
  const isServer = !(typeof window != 'undefined' && window.document);
  const requestId = useMemo(() => {
    return pageContext.requestId;
  }, [pageContext]);
  const sessionID = Cookies.get('sessionID');

  const currentTimestamp = Date.now();
  const currentDateTimeStamp = new Date(currentTimestamp).getTime();
  const { currentLocale } = useContext(PageParamsContext);

  const [dietList, setDietList] = useState([]);
  const [allergensList, setAllergenList] = useState([]);
  const [loadDietsList, { data: dietListData }] = useLazyQuery(lookupMenuDiets);
  const [loadAllergensList, { data: allergenListData }] = useLazyQuery(lookupMenuAllergens);

  const uxlRequestBody = {
    fetchPolicy: isServer ? 'network-only' : ('cache-first' as WatchQueryFetchPolicy),
    context: {
      headers: {
        // Any header, including x-request-id, can be passed in args with query.
        // If you don't pass it in the authLink will generate a random ID.
        'accept-language': currentLocale?.replace('_', '-') ?? 'en-US',
        'x-request-id': requestId,
        'x-b3-traceId': `${sessionID ?? sessionID ?? 'fallback-token'} - ${currentDateTimeStamp}`,
        'x-b3-spanId': requestId !== '' ? requestId : `${currentDateTimeStamp}`,
        'correlation-id': `${sessionID ?? sessionID ?? 'fallback-token'} - ${currentDateTimeStamp}`,
      },
    },
  };

  useEffect(() => {
    function handleResize() {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      portSize = useGetBreakpoint();
      setIsMobileViewPort(portSize === 'mobile' || portSize === 'tablet');
      setMenuLimit(portSize === 'mobile' || portSize === 'tablet' ? menuFooterListMobile : menuFooterListDesktop);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!props?.isAuthorMode) {
      loadDietsList(uxlRequestBody);
      loadAllergensList(uxlRequestBody);
    }
  }, []);

  useEffect(() => {
    if (dietListData && dietListData?.lookupMenuDiets?.length) setDietList(dietListData?.lookupMenuDiets);
    if (allergenListData && allergenListData?.lookupMenuAllergens?.length)
      setAllergenList(allergenListData?.lookupMenuAllergens);
  }, [dietListData, allergenListData]);

  const getList = (list: any, itemType: string) => {
    const data = list?.map((_: any, index: number) => {
      if (index % menuLimit === 0) {
        const dataList = [];
        for (let ind = index; ind <= index + (menuLimit - 1); ind++) {
          if (list[ind]) {
            dataList.push(
              <div className="mb-2" key={list[ind].label}>
                <DietAllergenIcon itemCode={list[ind].code} itemType={itemType} />
                <span className="ml-1 t-font-xs">{list[ind].label}</span>
              </div>
            );
          }
        }

        return <div>{dataList}</div>;
      }
      return null;
    });
    return data;
  };

  return (
    <StyledMenuModalFooter>
      <div className="menu-footer">
        <div
          className={clsx(
            isMobileViewPort ? 'flex-column-reverse align-items-center' : 'justify-content-between flex-row',
            'd-flex mb-2'
          )}
        >
          <div className={clsx(isMobileViewPort ? 'mt-2' : '')}>
            <span className="t-font-s">{props?.dietAndAllergenListLabel}</span>
            <Button
              type={Types.ButtonTypeVariation.Button}
              className="menu-footer__show-more-button t-font-xs ml-3 p-0"
              callback={props?.onToggle}
            >
              {props?.showList ? props?.showLabel || '' : props?.hideLabel || ''}
              <span className={clsx('ml-1', props?.showList ? 'icon-dropdown-down' : 'icon-dropdown-up')}></span>
            </Button>
          </div>

          <div>
            <span className="px-2 py-1 mr-2 menu-footer__diet-label t-font-xs">{props?.dietLabel}</span>
            <span className="px-2 py-1 menu-footer__allergen-label t-font-xs">{props?.allergenLabel}</span>
          </div>
        </div>

        {!props?.showList && (dietList?.length > 0 || allergensList?.length > 0) && (
          <div className="row menu-footer__list-container mx-0">
            {dietList?.length > 0 && (
              <div className={clsx('col-xs-12 col-sm-12 col-md-12 col-lg-6 col-xl-6 p-0')}>
                <span className="t-label-s pr-5">{props?.dietLabel}</span>
                <div className="d-flex mt-3 menu-footer__diet-list pr-5">{getList(dietList, dietType)}</div>
              </div>
            )}
            {allergensList?.length > 0 && (
              <div className={clsx('col-xs-12 col-sm-12 col-md-12 col-lg-6 col-xl-6 p-0')}>
                <span className="t-label-s pl-xs-0 pl-sm-0 pl-md-0 pl-lg-0 pl-xl-5">{props?.allergenLabel}</span>
                <div className="d-flex mt-3 menu-footer__allergen-list pl-xs-0 pl-sm-0 pl-md-0 pl-lg-0 pl-xl-5">
                  {getList(allergensList, allergenType)}
                </div>
              </div>
            )}
          </div>
        )}

        <p className={clsx('t-font-xs mb-1 text-left')}>{footerDisclaimer}</p>
      </div>
    </StyledMenuModalFooter>
  );
};
